import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Container,
  Tabs,
  Tab,
  Paper,
  Link,
  Grid,
  Stack,
  Divider,
  Card,
  CardContent,
  Button,
  useTheme,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';

// Icons
// import DashboardIcon from '@mui/icons-material/Dashboard'; // UNUSED
import VideocamIcon from '@mui/icons-material/Videocam';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ReceiptIcon from '@mui/icons-material/Receipt';
import InfoIcon from '@mui/icons-material/Info';
import RouterIcon from '@mui/icons-material/Router';
import HelpIcon from '@mui/icons-material/Help';
import MemoryIcon from '@mui/icons-material/Memory';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import DevicesIcon from '@mui/icons-material/Devices';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

// Custom components
import CameraSettings from './components/CameraSettings';
import LicenseSettingsView from './components/LicenseSettingsView';
import RouterSettingsView from './components/RouterSettingsView';
import PLCSettingsView from './components/PLCSettingsView';
import LogoutView from './components/LogoutView';
import AccountInfoView from './components/AccountInfoView';
import SettingsDeploymentManager from '../../components/SettingsDeploymentManager';
import { sessionService } from '../../service/session.service';
import { SettingsProvider } from '../../context/SettingsContext';

// Platzhalterkomponenten für Detailansichten
const ComingSoonView = () => (
  <Card>
    <CardContent>
      <Typography variant="h6" gutterBottom>Coming Soon</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography>Diese Funktion wird in Kürze verfügbar sein.</Typography>
      </Box>
    </CardContent>
  </Card>
);

const AnfragenView = () => (
  <Card>
    <CardContent>
      <Typography variant="h6" gutterBottom>Support Anfragen</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>

        <Typography>info@washprotech.de</Typography>

+49 173 4700004
      </Box>
    </CardContent>
  </Card>
);

const BackofficeButtonView = () => (
  <Card>
    <CardContent>
      <Typography variant="h6" gutterBottom>Backoffice Verwaltung</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        <Typography variant="body1" gutterBottom>
          Über das Backoffice können Sie die Systemeinstellungen verwalten und auf erweiterte Administratorfunktionen zugreifen.
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AdminPanelSettingsIcon />}
          onClick={() => window.location.href = '/backoffice'}
          sx={{ mt: 2 }}
        >
          Zum Backoffice
        </Button>
      </Box>
    </CardContent>
  </Card>
);

// Haupttypen für die Einstellungskategorien gemäß dem Screenshot
const settingsCategories = [
  {
    id: 'account',
    title: 'Account',
    icon: <AccountCircleIcon />,
    subcategories: [
      { 
        id: 'info', 
        title: 'Info', 
        icon: <InfoIcon />,
        component: <AccountInfoView />
      },
      { 
        id: 'lizenz', 
        title: 'Lizenz', 
        icon: <VpnKeyIcon />,
        component: <LicenseSettingsView />
      },
      { 
        id: 'rechnung', 
        title: 'Rechnung', 
        icon: <ReceiptIcon />,
        component: <ComingSoonView />
      },
      { 
        id: 'logout', 
        title: 'Abmelden', 
        icon: <LogoutIcon />,
        component: <LogoutView />
      }
    ]
  },
  {
    id: 'hardware',
    title: 'Hardware',
    icon: <DevicesIcon />,
    subcategories: [
      { 
        id: 'router', 
        title: 'Router', 
        icon: <RouterIcon />,
        component: <RouterSettingsView />
      },
      { 
        id: 'steuerung', 
        title: 'Waschstrassensteuerung', 
        icon: <MemoryIcon />,
        component: <PLCSettingsView />
      },
      { 
        id: 'kameras', 
        title: 'Kameras', 
        icon: <CameraAltIcon />,
        component: <CameraSettings />
      }
    ]
  },
  {
    id: 'support',
    title: 'Support Anfragen',
    icon: <HelpIcon />,
    subcategories: [
      { 
        id: 'anfragen', 
        title: 'Anfragen', 
        icon: <HelpIcon />,
        component: <AnfragenView />
      }
    ]
  }
];

const SettingsView: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(2); // 2 entspricht "Einstellungen" im Tab-Menü
  const [selectedCategory, setSelectedCategory] = useState('account');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>('info');
  const [showSubcategories, setShowSubcategories] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  // Dynamically build settings categories based on user role
  const getSettingsCategories = () => {
    const categories = [
      {
        id: 'account',
        title: 'Account',
        icon: <AccountCircleIcon />,
        subcategories: [
          { 
            id: 'info', 
            title: 'Info', 
            icon: <InfoIcon />,
            component: <AccountInfoView />
          },
          { 
            id: 'lizenz', 
            title: 'Lizenz', 
            icon: <VpnKeyIcon />,
            component: <LicenseSettingsView />
          },
          { 
            id: 'rechnung', 
            title: 'Rechnung', 
            icon: <ReceiptIcon />,
            component: <ComingSoonView />
          },
          { 
            id: 'logout', 
            title: 'Abmelden', 
            icon: <LogoutIcon />,
            component: <LogoutView />
          }
        ]
      },
      {
        id: 'hardware',
        title: 'Hardware',
        icon: <DevicesIcon />,
        subcategories: [
          { 
            id: 'router', 
            title: 'Router', 
            icon: <RouterIcon />,
            component: <RouterSettingsView />
          },
          { 
            id: 'steuerung', 
            title: 'Waschstrassensteuerung', 
            icon: <MemoryIcon />,
            component: <PLCSettingsView />
          },
          { 
            id: 'kameras', 
            title: 'Kameras', 
            icon: <CameraAltIcon />,
            component: <CameraSettings />
          }
        ]
      },
      {
        id: 'support',
        title: 'Support Anfragen',
        icon: <HelpIcon />,
        subcategories: [
          { 
            id: 'anfragen', 
            title: 'Anfragen', 
            icon: <HelpIcon />,
            component: <AnfragenView />
          }
        ]
      }
    ];
    
    // Add admin section if user is admin
    if (isAdmin) {
      categories.push({
        id: 'admin',
        title: 'Administration',
        icon: <AdminPanelSettingsIcon />,
        subcategories: [
          { 
            id: 'backoffice', 
            title: 'Backoffice', 
            icon: <AdminPanelSettingsIcon />,
            component: <BackofficeButtonView />
          }
        ]
      });
    }
    
    return categories;
  };

  // Prüfe, ob ein spezifischer Bereich geöffnet werden soll (z.B. Kamera-Einstellungen)
  useEffect(() => {
    if (location.state) {
      const { category, subcategory } = location.state as any;
      
      if (category) {
        setSelectedCategory(category);
        setShowSubcategories(true);
        
        if (subcategory) {
          setSelectedSubcategory(subcategory);
        } else {
          // Wähle die erste Unterkategorie, wenn keine spezifische angegeben wurde
          const firstSubcategory = getSettingsCategories().find(cat => cat.id === category)?.subcategories[0]?.id;
          if (firstSubcategory) {
            setSelectedSubcategory(firstSubcategory);
          }
        }
      }
    }
    
    // Check if user is admin
    const checkAdminStatus = async () => {
      try {
        const isAdminUser = await sessionService.isAdmin();
        setIsAdmin(isAdminUser);
        console.log('User is admin:', isAdminUser);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
    };
    
    checkAdminStatus();
  }, [location.state]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    
    // Navigation zu anderen Seiten basierend auf Tab-Auswahl
    switch (newValue) {
      case 0:
        navigate('/kameras');  // Kameras
        break;
      case 1:
        navigate('/meldungen');  // Meldungen
        break;
      case 2:
        // Wir sind bereits auf der Einstellungen-Seite
        break;
    }
  };

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
    // Wenn wir zu einer neuen Kategorie wechseln, wählen wir die erste Unterkategorie aus
    const firstSubcategory = getSettingsCategories().find(cat => cat.id === category)?.subcategories[0].id;
    setSelectedSubcategory(firstSubcategory || null);
    setShowSubcategories(true);
  };

  const handleSubcategoryClick = (subcategory: string) => {
    setSelectedSubcategory(subcategory);
  };

  const handleBackClick = () => {
    setShowSubcategories(false);
  };

  // Finde die aktuell ausgewählte Kategorie und Unterkategorie
  const currentCategory = getSettingsCategories().find(cat => cat.id === selectedCategory);
  const currentSubcategory = currentCategory?.subcategories.find(sub => sub.id === selectedSubcategory);

  return (
    <SettingsProvider>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Header mit Navigation */}
        <Box component="header" sx={{ mb: 0 }}>
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
              <Link 
                component="button" 
                onClick={() => navigate('/')}
                underline="none"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <Typography variant="h5" component="h1" sx={{ flexGrow: 1, fontWeight: 'bold', color: 'text.primary' }}>
                  WashProTech
                </Typography>
              </Link>
            </Box>
            
            <Paper 
              sx={{ 
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                marginBottom: 0
              }}
            >
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                aria-label="Navigation"
                variant="fullWidth"
                centered
                sx={{
                  marginBottom: 0,
                  paddingBottom: 0,
                  '& .MuiTabs-scroller': {
                    marginBottom: '0px !important'
                  },
                  '& .MuiTab-root': {
                    py: 2,
                    minHeight: '60px',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      bgcolor: 'transparent',
                      transition: 'background-color 0.3s'
                    },
                    '&.Mui-selected': {
                      fontWeight: 'bold',
                      color: 'primary.main',
                      '&::after': {
                        bgcolor: 'primary.main'
                      }
                    }
                  }
                }}
              >
                <Tab icon={<VideocamIcon />} label="Kameras" />
                <Tab icon={<NotificationsIcon />} label="Meldungen" />
                <Tab icon={<SettingsIcon />} label="Einstellung" />
              </Tabs>
            </Paper>
          </Container>
        </Box>

        {/* Hauptinhalt */}
        <Container maxWidth="lg" sx={{ flexGrow: 1, mb: 4, pt: 0 }}>
          <Box sx={{ display: 'flex', flex: 1, height: 'calc(100vh - 180px)' }}>
            <Grid container spacing={2}>
              {/* Linke Spalte: Entweder Hauptkategorien oder Unterkategorien */}
              <Grid item xs={12} md={3}>
                {!showSubcategories ? (
                  <Paper
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      boxShadow: 'none',
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      mb: 2,
                      position: 'sticky',
                      top: 16,
                      alignSelf: 'flex-start'
                    }}
                  >
                    <Stack spacing={2}>
                      {getSettingsCategories().map((category) => (
                        <Button
                          key={category.id}
                          variant={selectedCategory === category.id ? "contained" : "outlined"}
                          fullWidth
                          startIcon={category.icon}
                          onClick={() => handleCategoryClick(category.id)}
                          sx={{
                            justifyContent: 'flex-start',
                            px: 2,
                            py: 1.5,
                            borderRadius: 1,
                            textTransform: 'none',
                            fontWeight: 'medium',
                            fontSize: '1rem',
                          }}
                        >
                          {category.title}
                        </Button>
                      ))}
                      
                      {/* Speicher-Button als letzter Eintrag */}
                      <Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <SettingsDeploymentManager />
                        </Box>
                      </Box>
                    </Stack>
                  </Paper>
                ) : (
                  <Paper
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      boxShadow: 'none',
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      mb: 2,
                      position: 'sticky',
                      top: 16,
                      alignSelf: 'flex-start'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <IconButton 
                        onClick={handleBackClick}
                        size="small"
                        sx={{ mr: 1 }}
                      >
                        <ArrowBackIcon />
                      </IconButton>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {currentCategory?.title}
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <List disablePadding>
                      {currentCategory?.subcategories.map((subcategory) => (
                        <ListItemButton
                          key={subcategory.id}
                          selected={selectedSubcategory === subcategory.id}
                          onClick={() => handleSubcategoryClick(subcategory.id)}
                          sx={{
                            borderRadius: 1,
                            mb: 1,
                            '&.Mui-selected': {
                              backgroundColor: theme.palette.primary.light,
                              color: theme.palette.primary.contrastText,
                              '&:hover': {
                                backgroundColor: theme.palette.primary.main,
                              }
                            }
                          }}
                        >
                          <ListItemText primary={subcategory.title} />
                          <ArrowForwardIcon fontSize="small" />
                        </ListItemButton>
                      ))}
                    </List>
                    
                    {/* Speicher-Button als letzter Eintrag auch in Unterkategorien */}
                    <Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <SettingsDeploymentManager />
                      </Box>
                    </Box>
                  </Paper>
                )}
              </Grid>

              {/* Inhaltsbereich */}
              <Grid item xs={12} md={9}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 1,
                    boxShadow: 'none',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    minHeight: 300
                  }}
                >
                  {currentSubcategory ? (
                    currentSubcategory.component
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6">Bitte wählen Sie eine Unterkategorie</Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </SettingsProvider>
  );
};

export default SettingsView; 